import { createFileRoute } from "@tanstack/react-router";
import { Download, BarChart3 } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { spendData } from "@/lib/mock-data";
import { PageHeader } from "@/components/page-header";

export const Route = createFileRoute("/reports")({
  head: () => ({
    meta: [
      { title: "Reports & Analytics — ProcureFlow" },
      { name: "description", content: "Advanced procurement analytics, spend insights and vendor performance reports." },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="space-y-6">
      <PageHeader description="Insights across spend, cycle times and vendor performance." action={
        <button className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3.5 py-2 text-sm font-medium hover:bg-accent">
          <Download className="h-4 w-4" /> Export PDF
        </button>
      } />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-card border border-border p-5 shadow-elegant">
          <h3 className="text-sm font-semibold mb-4">Spend Trend</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={spendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="month" stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 12 }} />
              <Line type="monotone" dataKey="spend" stroke="var(--primary)" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="budget" stroke="var(--muted-foreground)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl bg-card border border-border p-5 shadow-elegant">
          <h3 className="text-sm font-semibold mb-4">Cycle Time by Department</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={[
              { name: "Engineering", value: 5.2 },
              { name: "Operations", value: 7.1 },
              { name: "Marketing", value: 4.4 },
              { name: "Facilities", value: 8.6 },
              { name: "IT", value: 3.8 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="name" stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="var(--muted-foreground)" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="value" fill="var(--chart-2)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: "Avg PR-to-PO Cycle", value: "5.6 days", change: "-12%" },
          { label: "Approval SLA", value: "94%", change: "+3%" },
          { label: "Vendor On-time Rate", value: "87.4%", change: "+1.2%" },
        ].map((m) => (
          <div key={m.label} className="rounded-2xl bg-card border border-border p-5 shadow-elegant">
            <BarChart3 className="h-5 w-5 text-primary" />
            <p className="text-xs text-muted-foreground mt-3">{m.label}</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-bold">{m.value}</span>
              <span className="text-xs font-medium [color:var(--success)]">{m.change}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
